import type { Session } from '../calendar.ts';
import { perUnitMove, signOf, type TradeType } from '../primitives.ts';
import { type Frame, rOf } from '../r.ts';
import { HOUR_MS, MINUTE_MS } from '../when.ts';

// Read-side only: an indicated price never reaches `record-price`, the watcher, autopilot or a GTT.

export const MARK_KINDS = ['CLOSE', 'SAMPLE'] as const;

/** A `CANDLE` lands after the last `TICK` and wins over it. */
export const PRICE_SOURCES = ['TICK', 'CANDLE'] as const;

export type PriceSource = (typeof PRICE_SOURCES)[number];

export const STALE_REASONS = ['reference', 'shut'] as const;

export type StaleReason = (typeof STALE_REASONS)[number];

/** Counted in minutes the reference traded: CME's 10-minute delay plus slack. */
export const INDICATION_STALE_MS = 20 * MINUTE_MS;

export const INDICATION_DEAD_MS = HOUR_MS;

/** A CME price entered by hand further than this from the close's is taken for a typo. */
export const ENTERED_MOVE_LIMIT = 0.2;

/** A reference read far from the bell folds the night's move into the ratio: wrong, yet plausible. */
const CLOSE_CATCH_MS = 15 * MINUTE_MS;

/** The strip runs ten minutes behind, so the bell's last minute is on it a quarter hour later. */
export const CLOSE_READ_AFTER_MS = 15 * MINUTE_MS;

/** Past this the close is missing, though it is asked for all night. */
const CLOSE_DUE_MS = 2 * CLOSE_READ_AFTER_MS;

export const caughtTheClose = (at: Date, bell: Date): boolean =>
  Math.abs(bell.getTime() - at.getTime()) <= CLOSE_CATCH_MS;

/** NSE's close is fixed at the end of its closing auction. */
export const bellOf = (session: Pick<Session, 'close' | 'phases'>): Date =>
  session.phases.find((one) => one.phase === 'CLOSING_AUCTION')?.to ?? session.close;

interface Indicates {
  contractToken: number;
  commodity: string;
  venue: string;
}

/**
 * The tracked contract, where every level is priced, whatever month a member holds; else an NSE
 * option's index, whose premiums are not (`levels` false).
 */
export function indicationFor<I extends Indicates>(
  recommendation: { trackingToken: number; trackingKind: string; underlying: string },
  indications: readonly I[],
): { indication: I; levels: boolean } | null {
  const tracked = indications.find((one) => one.contractToken === recommendation.trackingToken);
  if (tracked) return { indication: tracked, levels: true };
  const underlying = indications.find((one) => one.commodity === recommendation.underlying);
  return recommendation.trackingKind === 'OPTION' && underlying?.venue === 'NSE'
    ? { indication: underlying, levels: false }
    : null;
}

/** The level an indicated open is past, if any. */
export type Crossed = 'stoploss' | 'target' | null;

/** The stop first: a gapped stop fills at the open. */
export function crossedAt(
  indicated: number,
  levels: { tradeType: TradeType; targetPrice: number; stoplossPrice: number },
): Crossed {
  const up = signOf(levels.tradeType);
  if ((indicated - levels.stoplossPrice) * up < 0) return 'stoploss';
  if ((indicated - levels.targetPrice) * up > 0) return 'target';
  return null;
}

export interface OpenVerdict {
  kind: 'through-stop' | 'past-target' | 'profit' | 'against' | 'flat';
  /** In the frame's R at the indicated price; null where the stop measures no loss. */
  r: number | null;
  /** How far past the stop or the target, in R; null short of both, or with no R. */
  beyond: number | null;
}

const FLAT_R = 0.05;

export function openVerdict(frame: Frame, indicated: number): OpenVerdict {
  const r = rOf(frame, indicated);
  const crossed = crossedAt(indicated, frame);
  const past = (level: number): number | null => {
    const at = rOf(frame, level);
    return r === null || at === null ? null : Math.abs(r - at);
  };
  if (crossed === 'stoploss') return { kind: 'through-stop', r, beyond: past(frame.stoplossPrice) };
  if (crossed === 'target') return { kind: 'past-target', r, beyond: past(frame.targetPrice) };
  const move = perUnitMove(frame.tradeType, frame.entryPrice, indicated);
  const flat = r === null ? move === 0 : Math.abs(r) < FLAT_R;
  return { kind: flat ? 'flat' : move > 0 ? 'profit' : 'against', r, beyond: null };
}

export const pastFallback = (tradeType: TradeType, fallback: number, indicated: number): boolean =>
  signOf(tradeType) * (indicated - fallback) < 0;

/** Worst first. */
const NIGHT_FAULTS = [
  'missed-close',
  'late-close',
  'gone-quiet',
  'partial-close',
  'unpriced-close',
] as const;

export type NightFault = (typeof NIGHT_FAULTS)[number];

export interface NightStanding {
  bell: Date;
  now: Date;
  watched: number;
  marked: number;
  priced: number;
  referenceAt: Date | null;
  tradedSinceReading: number;
  mcxShut: boolean;
  referenceTrading: boolean;
}

export function faultOf(night: NightStanding): NightFault | null {
  const catching = night.now.getTime() - night.bell.getTime() < CLOSE_DUE_MS;
  if (night.marked === 0) return catching ? null : 'missed-close';
  if (night.referenceAt === null || !caughtTheClose(night.referenceAt, night.bell))
    return 'late-close';
  if (
    night.mcxShut &&
    night.referenceTrading &&
    !catching &&
    night.tradedSinceReading > INDICATION_DEAD_MS
  )
    return 'gone-quiet';
  if (night.marked < night.watched) return catching ? null : 'partial-close';
  if (night.priced < night.marked) return 'unpriced-close';
  return null;
}
